// frontend/src/components/RadioSwitcher.jsx
import React from "react";
import { authFetch } from "../authFetch";
import API_BASE_URL from "../api";

const LS_KEY = "selectedRadioShortcode";
const RADIO_ID_LS_KEY = "selectedRadioId";

export function getSelectedRadioShortcode() {
  try {
    return localStorage.getItem(LS_KEY) || null;
  } catch {
    return null;
  }
}

function persistSelectedRadio(shortcode, radio) {
  try {
    if (shortcode) localStorage.setItem(LS_KEY, shortcode);
    else localStorage.removeItem(LS_KEY);
  } catch {}

  try {
    const radioId = radio?.id ?? null;
    if (radioId != null) localStorage.setItem(RADIO_ID_LS_KEY, String(radioId));
    else localStorage.removeItem(RADIO_ID_LS_KEY);
  } catch {}
}

export default function RadioSwitcher({ onChange }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selected, setSelected] = React.useState(getSelectedRadioShortcode());

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const API = (API_BASE_URL || "").replace(/\/+$/, "");
        const res = await authFetch(`${API}/radios`, {
          method: "GET",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        // Оставляем только нужные поля, сортируем:
        // 1) с shortcode и connected — вперед
        // 2) с shortcode, но не connected
        // 3) без shortcode — в конец (disabled)
        const normalized = (Array.isArray(data) ? data : []).map((r) => ({
          id: r.id,
          name: r.name,
          shortcode: r.shortcode ?? null,
          status: r.status,
          country: r.country,
        }));

        const score = (r) =>
          (r.shortcode ? 2 : 0) + (String(r.status) === "connected" ? 1 : 0);
        normalized.sort((a, b) => score(b) - score(a) || a.name.localeCompare(b.name));

        if (!alive) return;
        setItems(normalized);

        // список валидных для сервисов: только с shortcode и status='connected'
        const valid = normalized.filter(r => !!r.shortcode && r.status === "connected");
        if (valid.length === 0) {
          persistSelectedRadio(null, null);
          // 🔕 важный сброс: шорткода нет — сервисы должны остановиться
          try {
            window.dispatchEvent(new CustomEvent("radio:change", {
              detail: { shortcode: null, radioId: null, radio: null },
            }));
          } catch {}
          setSelected(null);
        }

        // НИКАКОГО автовыбора — ждём явного клика пользователя.
        const saved = getSelectedRadioShortcode();
        const isValidSaved = saved && valid.some(r => r.shortcode === saved);
        if (isValidSaved) {
          setSelected(saved);
          persistSelectedRadio(saved, valid.find(r => r.shortcode === saved) || null);
        } else {
          setSelected(null);
          persistSelectedRadio(null, null);
        }
        // emitChange НЕ вызываем здесь — только в handleSelect()
      } catch (e) {
        if (!alive) return;
        setError(e.message || "Fetch error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [onChange]);

  function emitChange(shortcode, radio) {
    try {
      window.dispatchEvent(
        new CustomEvent("radio:change", {
          detail: {
            shortcode,
            radioId: radio?.id ?? null,
            radio,
          },
        })
      );
    } catch {}
  }

  function handleSelect(sc) {
    const radio = items.find(r => r.shortcode === sc && r.status === "connected") || null;
    if (!radio) return; // гейт: никакого выбора «левого»/неактивного шорткода
    setSelected(sc);
    persistSelectedRadio(sc, radio);
    emitChange(sc, radio);
    onChange?.(sc);
  }

  // Если у пользователя нет радио — ничего не показываем
  if (!loading && !error && items.length === 0) return null;

  return (
    <div className="radio-switcher" style={{ margin: "8px 0" }}>
      {loading && <div style={{ opacity: 0.7 }}>Завантаження радіостанцій…</div>}
      {error && (
        <div style={{ color: "#b00020" }}>
          Помилка завантаження списку радіо: {String(error)}
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div
          className="radio-switcher__list"
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {items.map((r) => {
            const isActive = selected && r.shortcode === selected;
            const disabled = !r.shortcode;
            const title = disabled
              ? "Статистика стане доступною після підключення (немає shortcode)"
              : `Переключити на ${r.name}`;

            return (
              <button
                key={r.id}
                type="button"
                title={title}
                disabled={disabled}
                onClick={() => handleSelect(r.shortcode)}
                className={`radio-switcher__btn${isActive ? " is-active" : ""}`}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid " + (isActive ? "#0b57d0" : "#ddd"),
                  background: isActive ? "#e8f0fe" : "#fff",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  fontSize: 14,
                }}
              >
                <span style={{ fontWeight: 600 }}>{r.name}</span>
                {r.country ? <span style={{ opacity: 0.7 }}> · {r.country}</span> : null}
                {disabled ? (
                  <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.6 }}>
                    (очікує підключення)
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
